'use client';

import { useState } from 'react';
import {
  Medal,
  Gift,
  ChatCircle,
  Cake,
  Coin,
  Percent,
  Star,
  ArrowRight,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { StatusRollsModal } from '@/components/promotions/status-rolls-modal';
import { FirstOrderModal } from '@/components/promotions/first-order-modal';
import { ReviewModal } from '@/components/promotions/review-modal';
import { BirthdayModal } from '@/components/promotions/birthday-modal';
import { LoyaltyModal } from '@/components/promotions/loyalty-modal';
import type { PublicPromotion } from '@/lib/api/content';

/**
 * Карточки акций.
 *
 * Список приходит с сервера (GET /api/content/promotions, см. lib/api/content.ts)
 * — это ответ на вопрос 49: включать и выключать акции владелец должен из
 * админки, без релиза. Раньше массив лежал прямо здесь, а «День рождения» была
 * закомментирована руками; теперь она никуда не удалена, а показывается ровно
 * тогда, когда включена переключателем в админке.
 *
 * С сервера приходят только тексты, ИМЯ иконки и КЛЮЧ палитры. Сами иконки и
 * градиенты остаются кодом: это дизайн, и он у сайта и приложения разный.
 * Неизвестное имя не роняет страницу — берётся запасное значение.
 */

type ActiveModal = 'statusRolls' | 'firstOrder' | 'review' | 'birthday' | 'loyalty' | null;

const MODAL_KEYS: readonly NonNullable<ActiveModal>[] = [
  'statusRolls',
  'firstOrder',
  'review',
  'birthday',
  'loyalty',
];

/** detail_key из базы → модалка. Пустой или незнакомый ключ = карточка без кнопки. */
function modalByDetailKey(detailKey: string | null): ActiveModal {
  if (!detailKey) return null;
  return MODAL_KEYS.find((key) => key === detailKey) ?? null;
}

/** Имя иконки из базы → компонент. */
const ICONS: Record<string, Icon> = {
  gift: Gift,
  medal: Medal,
  chat: ChatCircle,
  cake: Cake,
  coin: Coin,
  percent: Percent,
  star: Star,
};

/** Ключ палитры из базы → градиент карточки. */
const GRADIENTS: Record<string, string> = {
  'violet-deep': 'from-[var(--primary)] to-[var(--violet-700)]',
  'violet-bright': 'from-[var(--violet-500)] to-[var(--primary)]',
  'violet-soft': 'from-[var(--violet-600)] to-[var(--violet-400)]',
  'violet-royal': 'from-[var(--violet-600)] to-[var(--primary)]',
  pink: 'from-[var(--violet-700)] to-[var(--primary)]',
};

const FALLBACK_ICON = Gift;
const FALLBACK_GRADIENT = GRADIENTS['violet-deep'];

/**
 * Запасной список акций живёт в отдельном модуле без 'use client'
 * (./fallback-promos): его читает СЕРВЕРНЫЙ компонент app/actions/page.tsx,
 * а значение, импортированное сервером из клиентского модуля, — это ссылка на
 * модуль, а не массив (обращение к `.find` по ней падает). Здесь список не
 * нужен: карточки всегда получают готовый `promos` пропом.
 */

interface ActionsCardsProps {
  promos: PublicPromotion[];
}

export function ActionsCards({ promos }: ActionsCardsProps) {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  const promoByDetailKey = (key: NonNullable<ActiveModal>) =>
    promos.find((promo) => promo.detail_key === key);

  /**
   * Условия акции для подробного окна. undefined (а не пустой массив), если
   * акции нет в ответе или условия не заполнены: модалка в этом случае
   * покажет свой зашитый текст, а не пустой блок «Условия акции».
   */
  const termsFor = (key: NonNullable<ActiveModal>): string[] | undefined => {
    const terms = promoByDetailKey(key)?.terms;
    return terms && terms.length > 0 ? terms : undefined;
  };

  return (
    <section className="py-16 sm:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[var(--bg-secondary)]" />
      <div className="blur-blob absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[var(--primary)]/8 blur-[120px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12 sm:mb-16 animate-fade-up">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] text-sm font-semibold mb-4">
            <Gift weight="fill" className="w-4 h-4" />
            Акции
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black tracking-tight text-[var(--text-primary)] mb-4">
            Наши{' '}
            <span className="text-[var(--primary)]">предложения</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg max-w-2xl mx-auto">
            Выберите акцию и узнайте подробности
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {promos.map((promo, i) => {
            const Icon = ICONS[promo.icon] ?? FALLBACK_ICON;
            const gradient = GRADIENTS[promo.accent] ?? FALLBACK_GRADIENT;
            const modal = modalByDetailKey(promo.detail_key);

            const card = (
              <div
                className={cn(
                  'relative rounded-2xl bg-[var(--surface-primary)] border border-[var(--border-default)]',
                  'p-6 h-full transition-all duration-300',
                  'hover:shadow-xl hover:shadow-[var(--primary)]/5 hover:-translate-y-1 hover:border-[var(--border-hover)]',
                )}
              >
                <div
                  className={cn(
                    'w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center mb-5',
                    'shadow-lg shadow-[var(--primary)]/15 transition-transform duration-300 group-hover:scale-110',
                    gradient,
                  )}
                >
                  <Icon weight="fill" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2">{promo.title}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
                  {promo.description}
                </p>
                {modal && (
                  <span className="inline-flex items-center gap-1.5 mt-1 px-4 py-2 rounded-xl bg-[var(--primary-subtle)] text-sm font-semibold text-[var(--primary)] transition-colors duration-200 group-hover:bg-[var(--primary)] group-hover:text-white">
                    Подробнее
                    <ArrowRight weight="bold" className="w-4 h-4" />
                  </span>
                )}
              </div>
            );

            // Акция без подробного окна — не кнопка: нажатие ничего не откроет,
            // а кликабельная карточка, которая молчит, читается как поломка.
            return modal ? (
              <button
                key={promo.slug}
                onClick={() => setActiveModal(modal)}
                className="group relative animate-fade-up text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30 rounded-2xl"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {card}
              </button>
            ) : (
              <div
                key={promo.slug}
                className="group relative animate-fade-up rounded-2xl"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {card}
              </div>
            );
          })}
        </div>
      </div>

      {/*
        Модалки получают условия с сервера. Именно они — публичная оферта по
        акции, и именно их владелец правит в админке и публикует кнопкой
        «Опубликовать условия» (вопрос 50). Не пришли — модалка покажет свой
        зашитый текст, а не пустой блок.
      */}
      <StatusRollsModal
        open={activeModal === 'statusRolls'}
        onClose={() => setActiveModal(null)}
        terms={termsFor('statusRolls')}
      />
      <FirstOrderModal
        open={activeModal === 'firstOrder'}
        onClose={() => setActiveModal(null)}
        terms={termsFor('firstOrder')}
      />
      <ReviewModal
        open={activeModal === 'review'}
        onClose={() => setActiveModal(null)}
        terms={termsFor('review')}
      />
      <BirthdayModal
        open={activeModal === 'birthday'}
        onClose={() => setActiveModal(null)}
        terms={termsFor('birthday')}
      />
      <LoyaltyModal
        open={activeModal === 'loyalty'}
        onClose={() => setActiveModal(null)}
        terms={termsFor('loyalty')}
        legalNote={promoByDetailKey('loyalty')?.legal_note ?? null}
      />
    </section>
  );
}
